// LWI-592 — Persist the post-login redirect target across the entire auth
// flow, not just a single React Router navigation.
//
// The previous mechanism (AuthGuard → Navigate(state: { from })) loses the
// `from` value any time the auth flow takes a detour: 2FA challenges, 2FA
// setup, the standalone tenant picker (/select-tenant), or the SSO callback
// all `navigate()` between steps and don't carry router state forward. By
// the time the final goHome() fires after a multi-step login, location.state
// is undefined and the user lands on `/` instead of their original deep link.
//
// Solution: stash the target in sessionStorage on the way in, consume it on
// successful auth completion. sessionStorage is per-tab and auto-clears on
// tab close — matches the per-tab impersonation isolation introduced in
// LWI-582 — so it never leaks between sessions and there's no cross-tab
// surprise.

const KEY = "auth_redirect_after_login";

/**
 * TTL for a stashed redirect target. Long enough to absorb a slow MFA / SSO
 * round-trip; short enough that an abandoned bounce doesn't surprise the
 * user a session later. Tuning knob — bump if real flows hit the cap.
 */
const TTL_MS = 10 * 60 * 1000;

/**
 * Returns true if `target` is a safe internal path the app may redirect to
 * after login. Rejects:
 *   - empty / non-string
 *   - absolute URLs (http://, https://) and protocol-relative (//evil.com)
 *   - the login route itself (would cause a redirect loop)
 *   - the /sso/callback route (only meaningful with its original ticket)
 *
 * The /login and /sso/callback checks use exact-or-boundary matching so
 * legitimate paths like /login-help or a future /sso/dashboard don't get
 * caught by an over-broad prefix.
 */
function isSafeInternalPath(target) {
  if (!target || typeof target !== "string") return false;
  if (!target.startsWith("/")) return false;
  if (target.startsWith("//")) return false;
  if (matchesRoute(target, "/login")) return false;
  if (matchesRoute(target, "/sso/callback")) return false;
  return true;
}

/**
 * True if `target` represents a navigation to `route` exactly, or a path
 * within `route` (separated by `/`, `?`, or `#`). Distinguishes `/login`
 * from `/login-help` while still catching `/login/recover`, `/login?...`,
 * and `/login#...`.
 */
function matchesRoute(target, route) {
  if (target === route) return true;
  const next = target[route.length];
  return target.startsWith(route) && (next === "/" || next === "?" || next === "#");
}

/**
 * Stash a redirect target. Call from any place that's about to send the user
 * to /login because they're not yet authenticated (typically AuthGuard).
 * Pass `pathname + search + hash` so deep links with query strings/hashes
 * survive the round-trip.
 */
export function setRedirectAfterLogin(target) {
  if (!isSafeInternalPath(target)) return;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ path: target, ts: Date.now() })
    );
  } catch {
    // sessionStorage can throw under quota / private mode; degrade silently.
  }
}

/**
 * Read the stashed entry without removing it. Returns null when missing,
 * malformed, expired, or the path no longer passes validation. Used both
 * by `consume` and by callers that need to peek (e.g. SelectTenant Cancel).
 */
function readStash() {
  let raw = null;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Older deploys wrote the bare string. Treat as a one-time, no-TTL stash
    // so an in-flight upgrade doesn't lose the user's deep link, but drop
    // it once consumed (no rewrite — just return).
    return isSafeInternalPath(raw) ? raw : null;
  }
  if (!parsed || typeof parsed.path !== "string" || typeof parsed.ts !== "number") {
    return null;
  }
  if (Date.now() - parsed.ts > TTL_MS) {
    // Lazy expiry: clear stale entry on read so a future stash isn't
    // shadowed by an old one.
    try { sessionStorage.removeItem(KEY); } catch {}
    return null;
  }
  return isSafeInternalPath(parsed.path) ? parsed.path : null;
}

/**
 * Pull the stashed redirect target and clear it. Call from any successful
 * post-auth landing point (Login.goHome, SelectTenant.handleSelect,
 * SsoCallback.goSmart). Returns null if nothing was stashed.
 *
 * Caller is responsible for the final fallback (typically `"/"`) — keeping
 * this function null-on-miss lets the caller distinguish "we have a target"
 * from "we don't" if it cares.
 */
export function consumeRedirectAfterLogin() {
  const target = readStash();
  if (target !== null) {
    try { sessionStorage.removeItem(KEY); } catch {}
  }
  return target;
}

/**
 * Peek at the stashed target without removing it. Used by SelectTenant when
 * it wants the Cancel button to know about the eventual destination without
 * draining the stash before the success path can consume it.
 */
export function peekRedirectAfterLogin() {
  return readStash();
}

/** Drop the stash without consuming. Useful when an explicit user action
 *  (logout, declining an invite) should reset the post-login target so the
 *  next login doesn't bounce them back to where they just left. */
export function clearRedirectAfterLogin() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
